/* WP-10 minimal markdown renderer for portal legal docs + FAQ answers.
 * Supports paragraphs, **bold**, *italic*, and [label](https|/relative) links.
 * Everything is built as React text nodes — no HTML injection surface. */
import type { ReactNode } from 'react';

const LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;
const ITALIC_RE = /\*([^*\n]+)\*/g;

function safeHref(url: string): string | null {
  if (url.startsWith('/')) return url;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'mailto:' || u.protocol === 'tel:'
      ? url
      : null;
  } catch {
    return null;
  }
}

function renderInline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  // Split on links first, then bold/italic inside each text segment.
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text))) {
    if (m.index > last) out.push(...renderEmphasis(text.slice(last, m.index), `${keyBase}-t${i}`));
    const href = safeHref(m[2]);
    out.push(
      href ? (
        <a key={`${keyBase}-l${i}`} href={href} target="_blank" rel="noreferrer">
          {m[1]}
        </a>
      ) : (
        m[1]
      ),
    );
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) out.push(...renderEmphasis(text.slice(last), `${keyBase}-t${i}`));
  return out;
}

function renderEmphasis(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  BOLD_RE.lastIndex = 0;
  while ((m = BOLD_RE.exec(text))) {
    if (m.index > last) out.push(...renderItalic(text.slice(last, m.index), `${keyBase}-i${i}`));
    out.push(<strong key={`${keyBase}-b${i}`}>{m[1]}</strong>);
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) out.push(...renderItalic(text.slice(last), `${keyBase}-i${i}`));
  return out;
}

function renderItalic(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  ITALIC_RE.lastIndex = 0;
  while ((m = ITALIC_RE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<em key={`${keyBase}-e${i}`}>{m[1]}</em>);
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Renders a small markdown document as paragraphs. */
export function MarkdownBody({ text, className }: { text: string; className?: string }) {
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className={className}>
      {paragraphs.map((p, idx) => (
        <p key={idx}>{renderInline(p.replace(/\n/g, ' '), `p${idx}`)}</p>
      ))}
    </div>
  );
}
